// ABOUTME: Query results display component with document list and metadata
// ABOUTME: Shows results with collection filter, refresh button, and pagination controls

import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { QueryResultsData } from './types';
import { PaginationControls } from './PaginationControls';

interface QueryResultsProps {
  queryData: QueryResultsData | null;
  isLoading: boolean;
  error: Error | null;
  multiCollectionMode: boolean;
  collectionFilter: string;
  collections: string[];
  pageSize: number;
  currentPage: number;
  showJson: boolean;
  queryJson: string;
  onRefresh: () => void;
  onCollectionFilterChange: (filter: string) => void;
  onPageSizeChange: (size: number) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  onDocumentClick: (docId: string, collection: string) => void;
  onCopyJson: () => void;
}

export const QueryResults: React.FC<QueryResultsProps> = ({
  queryData,
  isLoading,
  error,
  multiCollectionMode,
  collectionFilter,
  collections,
  pageSize,
  currentPage,
  showJson,
  queryJson,
  onRefresh,
  onCollectionFilterChange,
  onPageSizeChange,
  onNextPage,
  onPrevPage,
  onDocumentClick,
  onCopyJson,
}) => {
  const results = queryData?.results || [];
  const metadata = queryData?.metadata || null;

  return (
    <Card style={{ backgroundColor: 'var(--bg-darker)', border: '1px solid var(--border-color)' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--border-color)', padding: '1.5rem 1.5rem 1rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <CardTitle style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-dark)' }}>
            Results
          </CardTitle>
          {results.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {multiCollectionMode && (
                <select
                  value={collectionFilter}
                  onChange={(e) => onCollectionFilterChange(e.target.value)}
                  style={{ 
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    padding: '0.375rem 2rem 0.375rem 0.75rem',
                    backgroundColor: 'var(--bg-light)',
                    color: 'var(--text-dark)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.375rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.5rem center',
                    backgroundSize: '12px',
                    width: 'auto',
                    minWidth: 'fit-content'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                    e.currentTarget.style.backgroundColor = 'var(--bg-darker)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.backgroundColor = 'var(--bg-light)';
                  }}
                >
                  <option value="">All collections</option>
                  {collections.filter(c => c.trim() !== '').map((col, index) => (
                    <option key={index} value={col}>{col}</option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={onRefresh}
                disabled={isLoading}
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  fontSize: '0.875rem', 
                  fontWeight: 500,
                  padding: '0.375rem 0.75rem',
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.6 : 1,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.opacity = '0.9';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.opacity = '1';
                  }
                }}
              >
                <RefreshCw size={14} />
                Refresh
              </button>
            </div>
          )}
        </div>
        <CardDescription style={{ fontSize: '0.875rem', color: 'var(--text-medium)', fontWeight: 500 }}>
          {metadata ? (
            <>
              <span style={{ color: 'var(--text-dark)', fontWeight: 600 }}>
                Fetched {metadata.count} document{metadata.count !== 1 ? 's' : ''}
              </span>
              <span style={{ marginLeft: '0.5rem', opacity: 0.7 }}>
                (Page size: {pageSize})
              </span>
              {metadata.hasMore && (
                <span style={{ color: 'var(--primary)', marginLeft: '0.5rem', fontWeight: 600 }}>
                  • More results available
                </span>
              )}
              <span style={{ marginLeft: '0.75rem', opacity: 0.7 }}>
                {metadata.queryTime}
              </span>
            </>
          ) : results.length > 0 ? (
            <span style={{ color: 'var(--text-dark)', fontWeight: 600 }}>
              {results.length} document{results.length !== 1 ? 's' : ''}
            </span>
          ) : (
            'No results yet'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent style={{ padding: '1.5rem' }}>
        {error && (
          <div
            style={{
              backgroundColor: 'var(--danger-bg)',
              border: '1px solid var(--danger)',
              borderRadius: '0.375rem',
              padding: '1rem',
              marginBottom: '1rem',
              color: 'var(--danger)',
            }}
          >
            <strong>Error:</strong> {error.message}
          </div>
        )}

        {showJson && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>Query JSON</label>
              <Button
                type="button"
                size="sm"
                onClick={onCopyJson}
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  backgroundColor: 'var(--primary-color)',
                  color: 'white',
                  border: 'none'
                }}
              >
                Copy
              </Button>
            </div>
            <pre
              style={{
                backgroundColor: 'var(--code-bg)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.375rem',
                padding: '1rem',
                overflow: 'auto',
                fontSize: '0.875rem',
                color: 'var(--code-text)',
                maxHeight: '300px',
              }}
            >
              {queryJson}
            </pre>
          </div>
        )}

        {results.length === 0 && !error && !isLoading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem 2rem',
            color: 'var(--text-medium)'
          }}>
            <p style={{ fontSize: '0.875rem', margin: 0, fontStyle: 'italic' }}>
              No results yet. Configure your query and click "Execute Query".
            </p>
          </div>
        )}

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <p style={{ color: 'var(--text-medium)', fontSize: '0.875rem', margin: 0 }}>
              Loading...
            </p>
          </div>
        )}

        {results.length > 0 && (
          <>
            {(!multiCollectionMode || collectionFilter) && (
              <PaginationControls
                pageSize={pageSize}
                currentPage={currentPage}
                metadata={metadata}
                onPageSizeChange={onPageSizeChange}
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                disabled={isLoading}
              />
            )}
            
            {multiCollectionMode && !collectionFilter && (
              <div style={{
                padding: '1rem',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-darker)',
              }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-medium)', margin: 0 }}>
                  Showing all results from {collections.filter(c => c.trim()).length} collections. 
                  Use the collection filter above to enable pagination for a specific collection.
                </p>
              </div>
            )}
            
            <div className="results-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {results.map((doc, index) => (
                <div
                  key={doc._id || index}
                  style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    backgroundColor: 'var(--bg-light)',
                    boxShadow: 'var(--shadow-sm)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => onDocumentClick(doc._id, doc._collection)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: 'var(--primary)', fontSize: '0.875rem' }}>
                        ID: {doc._id}
                      </span>
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.125rem 0.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        borderRadius: '0.25rem',
                        fontWeight: 500,
                        textTransform: 'uppercase'
                      }}>
                        {doc._collection}
                      </span>
                    </div>
                    <ExternalLink size={16} style={{ color: 'var(--text-medium)', flexShrink: 0 }} />
                  </div>
                  <pre style={{
                    fontSize: '0.75rem',
                    margin: 0,
                    padding: '0.75rem',
                    backgroundColor: 'var(--code-bg)',
                    borderRadius: '0.375rem',
                    color: 'var(--code-text)',
                    border: '1px solid var(--border-color)',
                  }}>
                    {JSON.stringify(doc, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

